import Flex from 'components/common/Flex';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import paths from 'routes/paths';
import InvoiceService from 'services/Invoice.service';
import InvoiceEditView from './InvoiceEditView';

const InvoiceDetailPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showErrorNotification } = useNotification();
  const [invoiceId, setInvoiceId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const codeRef = useRef(code);

  // Atualizar ref quando code mudar
  useEffect(() => {
    codeRef.current = code;
    // Resetar flag quando code mudar para permitir nova busca
    hasFetchedRef.current = false;
  }, [code]);

  useEffect(() => {
    // Evitar múltiplas execuções
    if (hasFetchedRef.current || !codeRef.current) {
      return;
    }

    const fetchInvoiceId = async () => {
      hasFetchedRef.current = true;
      setIsLoading(true);
      try {
        // Primeiro, tenta obter o ID do state (se veio da navegação da lista)
        const stateInvoiceId = location.state?.invoiceId;

        if (stateInvoiceId) {
          setInvoiceId(stateInvoiceId);
          setIsLoading(false);
          return;
        }

        // Se não tiver no state, busca na lista de faturas procurando pelo código
        const decodedCode = decodeURIComponent(codeRef.current);
        let foundInvoice = null;
        let currentPage = 0;
        const pageSize = 100;

        // Busca em várias páginas até encontrar a fatura ou não ter mais páginas
        while (!foundInvoice) {
          const queryParams = new URLSearchParams({
            page: currentPage,
            size: pageSize
          }).toString();

          const response = await InvoiceService.list(queryParams);

          if (!response || !response.content || response.content.length === 0) {
            break; // Não há mais páginas
          }

          // Procura a fatura com o código correspondente
          foundInvoice = response.content.find(
            inv =>
              inv.code === decodedCode ||
              String(inv.code) === String(decodedCode)
          );

          if (foundInvoice) {
            break; // Encontrou a fatura
          }

          // Se não encontrou e ainda há mais páginas, continua buscando
          if (currentPage >= response.totalPages - 1) {
            break; // Não há mais páginas
          }

          currentPage++;
        }

        if (foundInvoice && foundInvoice.id) {
          setInvoiceId(foundInvoice.id);
        } else {
          showErrorNotification('Fatura não encontrada');
          navigate(paths.billing);
        }
      } catch (error) {
        console.error('Erro ao buscar fatura:', error);
        showErrorNotification('Erro ao carregar fatura');
        navigate(paths.billing);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceId();
  }, [code]);

  const handleBack = () => {
    navigate(paths.billing);
  };

  const handleRefresh = () => {
    // Recarregar a página de detalhes se necessário
    if (code) {
      window.location.reload();
    }
  };

  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <Flex justifyContent="center" className="py-5">
            <Spinner animation="border" variant="primary" />
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  if (!invoiceId) {
    return null;
  }

  return (
    <InvoiceEditView
      invoiceId={invoiceId}
      onBack={handleBack}
      onRefresh={handleRefresh}
    />
  );
};

export default InvoiceDetailPage;
